import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Image as ImageIcon, Upload, Trash2, RefreshCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CategoryImagesAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileInputs = useRef({});

  function load() {
    setLoading(true);
    const tok = localStorage.getItem('token');
    axios.get(`${API}/admin/categories`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => setRows(r.data.items || []))
      .catch(() => toast.error('Failed to load categories'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  function pickFile(name) {
    const el = fileInputs.current[name];
    if (el) el.click();
  }

  async function upload(name, file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploadingFor(name);
    try {
      const tok = localStorage.getItem('token');
      const form = new FormData();
      form.append('file', file);
      form.append('category_name', name);
      const res = await axios.post(`${API}/admin/categories/upload-image`, form, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      toast.success(`Image saved for "${name}"`);
      setRows((prev) => prev.map((r) => (r.raw_name === name || r.name === name)
        ? { ...r, image_url: res.data.image_url } : r));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFor(null);
    }
  }

  async function removeImage(name) {
    if (!window.confirm(`Remove image for "${name}"?`)) return;
    try {
      const tok = localStorage.getItem('token');
      await axios.delete(`${API}/admin/categories/${encodeURIComponent(name)}/image`,
        { headers: { Authorization: `Bearer ${tok}` } });
      toast.success('Image removed');
      setRows((prev) => prev.map((r) => (r.raw_name === name || r.name === name)
        ? { ...r, image_url: null } : r));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  }

  // DB may hold either `/api/files/...` (new object storage), `/api/uploads/...`
  // (pre-fix local-disk era) or `/uploads/...` (legacy). Ingress only routes
  // `/api/*` to the backend, so rewrite legacy paths. Local-disk files were
  // wiped on each deploy — we surface a "lost on deploy" indicator below when
  // the image fails to load so admins know to re-upload.
  function fullUrl(url) {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads/')) return `${process.env.REACT_APP_BACKEND_URL}/api${url}`;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }

  function isLegacyLocalDisk(url) {
    // Anything served from local disk on previous deploys is now gone.
    return !!url && (url.startsWith('/uploads/') || url.startsWith('/api/uploads/'));
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-orange-600" />
          Category Images
        </CardTitle>
        <p className="text-xs text-slate-500">
          Upload an image per category — these appear in the Store Portal&apos;s &quot;Shop by Category&quot; strip.
          Recommended: square JPG/PNG, ~512×512px, under 5&nbsp;MB.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-end mb-3">
          <Button variant="outline" size="sm" onClick={load} data-testid="cat-images-refresh">
            <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-10 text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-slate-400">No categories yet.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {rows.map((r) => {
              const key = r.raw_name || r.name;
              const uploading = uploadingFor === key;
              return (
                <div key={r.name} className="border rounded-lg p-3 bg-white flex flex-col items-center gap-2"
                     data-testid={`cat-tile-${r.name}`}>
                  <div className="w-24 h-24 rounded-xl bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center relative">
                    {r.image_url ? (
                      <>
                        <img src={fullUrl(r.image_url)} alt={r.name}
                             className="w-full h-full object-cover"
                             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        {isLegacyLocalDisk(r.image_url) && (
                          <div className="absolute inset-0 bg-red-50/95 flex flex-col items-center justify-center p-1 text-center">
                            <ImageIcon className="w-5 h-5 text-red-400" />
                            <p className="text-[9px] text-red-700 font-semibold leading-tight mt-1">
                              Lost on deploy
                            </p>
                            <p className="text-[8px] text-red-600 leading-tight">Re-upload</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <ImageIcon className="w-8 h-8 text-slate-300" />
                    )}
                  </div>
                  <p className="text-sm font-medium text-slate-800 text-center line-clamp-2 leading-tight">
                    {r.name}
                  </p>
                  <p className="text-[10px] text-slate-400">{r.product_count} product{r.product_count !== 1 ? 's' : ''}</p>
                  <div className="flex gap-1 mt-1">
                    <input
                      ref={(el) => { fileInputs.current[key] = el; }}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => upload(key, e.target.files?.[0])}
                      data-testid={`cat-file-${r.name}`}
                    />
                    <Button size="sm" variant={r.image_url ? 'outline' : 'default'}
                            onClick={() => pickFile(key)} disabled={uploading}
                            className="h-7 text-xs"
                            data-testid={`cat-upload-${r.name}`}>
                      <Upload className="w-3 h-3 mr-1" />
                      {uploading ? 'Uploading…' : r.image_url ? 'Replace' : 'Upload'}
                    </Button>
                    {r.image_url && (
                      <Button size="sm" variant="ghost" className="h-7 text-red-600 px-2"
                              onClick={() => removeImage(key)}
                              data-testid={`cat-remove-${r.name}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
